import { memo } from 'react'
import type { SlideElement as SlideElementType } from '~/design-system/components/DeckGen/types/presentation'
import { getTextWidthAuto } from '~/design-system/components/DeckGen/utils/textSizeMode'
import { sanitizeInlineHtml } from '~/design-system/components/DeckGen/utils/sanitizeHtml'
import { effectiveBoxShadow, filterStyle, rotationTransform, shapeBackgroundStyle, shapeBorderRadius, shapeBorderStyle, strokeOverlayStyle, textGradientStyle } from '~/design-system/components/DeckGen/utils/elementRenderStyle'
import { gradientToCss } from '~/design-system/components/DeckGen/utils/gradientFill'

/**
 * Static, non-interactive render of an element's visual (text or shape) at full size. Used by the
 * drag ghost so a dragged-out element paints identically to its in-slide self. Mirrors the
 * presentational branches of `SlideElement` without any editing/resizing machinery.
 */
export const ElementVisual = memo(function ElementVisual({ element }: { element: SlideElementType }) {
  if (element.type === 'text') {
    const widthAuto = getTextWidthAuto(element.textSizeMode)
    const isBulleted = element.listStyle === 'bullet'
    const isNumbered = element.listStyle === 'number'
    const isList = isBulleted || isNumbered
    const gradient = textGradientStyle(element)
    // Preserved inline formatting (bold/italic/colored runs, AND inline ul/ol lists) renders as sanitized
    // HTML; only a plain or whole-element-list box (no textHtml) takes the simple path.
    const rich = !!element.textHtml
    const listItems = (element.text ?? '')
      .split('\n')
      .filter((line) => line.trim().length > 0)
      .map((line, index) => <li key={index}>{line}</li>)
    return (
      <div
        className="dg-text-content h-full w-full rounded-md"
        data-text-align={element.textAlign ?? 'left'}
        style={{
          fontSize: element.fontSize,
          lineHeight: element.lineHeight,
          fontFamily: element.fontFamily,
          fontWeight: element.fontWeight,
          fontStyle: element.fontStyle,
          letterSpacing: element.letterSpacing != null ? `${element.letterSpacing}px` : undefined,
          textAlign: element.textAlign,
          color: gradient ? 'transparent' : element.color,
          background: element.background,
          ...gradient,
          textShadow: element.textShadow,
          opacity: element.opacity != null ? element.opacity / 100 : undefined,
          transform: rotationTransform(element),
          ...filterStyle(element),
          whiteSpace: widthAuto ? 'pre' : 'pre-wrap',
          wordBreak: widthAuto ? 'normal' : 'break-word',
        }}
        {...(rich ? { dangerouslySetInnerHTML: { __html: sanitizeInlineHtml(element.textHtml!) } } : {})}
      >
        {rich ? null : isNumbered ? (
          <ol className="m-0 h-full list-decimal pl-[1.5em]">{listItems}</ol>
        ) : isBulleted ? (
          <ul className="m-0 h-full list-disc pl-[1.5em]">{listItems}</ul>
        ) : (
          element.text
        )}
      </div>
    )
  }

  const uniformRadius = Math.max(
    0,
    Math.min(element.cornerRadius ?? 0, Math.floor(Math.min(element.width, element.height) / 2)),
  )
  const borderRadius =
    element.shapeKind === 'circle' ? '50%' : shapeBorderRadius(element, element.width, element.height, uniformRadius)
  const strokeOverlay = strokeOverlayStyle(element, borderRadius)

  // Gradient fills resolve from the structured gradient against this element's box (keeps a start/end
  // linear axis exact); falls back to the stored `background` string otherwise.
  const shapeElement =
    element.fillGradient && element.shapeFillMode !== 'image'
      ? { ...element, background: gradientToCss(element.fillGradient, { width: element.width, height: element.height }) }
      : element

  return (
    <div
      className="h-full w-full"
      style={{
        ...shapeBackgroundStyle(shapeElement),
        ...shapeBorderStyle(element),
        borderRadius,
        boxShadow: effectiveBoxShadow(element),
        opacity: element.opacity != null ? element.opacity / 100 : undefined,
        transform: rotationTransform(element),
        ...filterStyle(element),
        boxSizing: 'border-box',
        position: 'relative',
      }}
    >
      {strokeOverlay ? <div style={strokeOverlay} /> : null}
    </div>
  )
})
